import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api, ApiClientError } from '../lib/api';
import {
  Card,
  Button,
  Input,
  Textarea,
  PageHeader,
  Spinner,
  EmptyState,
  Modal,
  Field,
  Select,
  Badge,
  ErrorBanner,
  formatCents,
} from '../components/ui';

interface Tier {
  id: string;
  name: string;
  description?: string;
  priceCents: number;
  currency: string;
  interval: 'month' | 'year';
  active: boolean;
}

export function MembershipsPage() {
  const [editing, setEditing] = useState<Tier | null>(null);
  const [showCreate, setShowCreate] = useState(false);
  const qc = useQueryClient();

  const q = useQuery({
    queryKey: ['membership-tiers'],
    queryFn: () => api.get<{ data: Tier[] }>('/membership-tiers').catch(() => ({ data: [] })),
  });

  const tiers = q.data?.data ?? [];

  return (
    <div>
      <PageHeader
        title="Memberships"
        subtitle={`${tiers.length} tiers`}
        action={<Button onClick={() => setShowCreate(true)}>+ New tier</Button>}
      />

      {q.isLoading ? (
        <Spinner />
      ) : tiers.length === 0 ? (
        <Card>
          <EmptyState
            title="No membership tiers yet"
            description="Sell access on a recurring basis: monthly, yearly, or both."
            action={<Button onClick={() => setShowCreate(true)}>+ New tier</Button>}
          />
        </Card>
      ) : (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: 'var(--space-3)' }}>
          {tiers.map((t) => (
            <Card key={t.id} onClick={() => setEditing(t)}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start' }}>
                <h3 style={{ margin: 0, fontSize: 'var(--text-lg)', fontWeight: 700 }}>{t.name}</h3>
                {!t.active && <Badge color="gray">inactive</Badge>}
              </div>
              <div style={{ fontSize: 'var(--text-2xl)', fontWeight: 700, color: 'var(--color-brand-primary)', margin: 'var(--space-2) 0' }}>
                {formatCents(t.priceCents, t.currency)}
                <span style={{ fontSize: 'var(--text-sm)', color: 'var(--color-text-tertiary)' }}>/{t.interval}</span>
              </div>
              {t.description && (
                <p style={{ fontSize: 'var(--text-sm)', color: 'var(--color-text-secondary)' }}>{t.description}</p>
              )}
            </Card>
          ))}
        </div>
      )}

      {(showCreate || editing) && (
        <TierModal
          tier={editing}
          onClose={() => {
            setShowCreate(false);
            setEditing(null);
          }}
          onSaved={() => {
            qc.invalidateQueries({ queryKey: ['membership-tiers'] });
            setShowCreate(false);
            setEditing(null);
          }}
        />
      )}
    </div>
  );
}

function TierModal({
  tier,
  onClose,
  onSaved,
}: {
  tier: Tier | null;
  onClose: () => void;
  onSaved: () => void;
}) {
  const [name, setName] = useState(tier?.name ?? '');
  const [description, setDescription] = useState(tier?.description ?? '');
  const [price, setPrice] = useState(tier ? (tier.priceCents / 100).toFixed(2) : '0.00');
  const [interval, setInterval] = useState<Tier['interval']>(tier?.interval ?? 'month');
  const [active, setActive] = useState(tier?.active ?? true);
  const [error, setError] = useState<string | null>(null);

  const saveMut = useMutation({
    mutationFn: () => {
      const body = {
        name,
        description: description || undefined,
        priceCents: Math.round((Number(price) || 0) * 100),
        currency: tier?.currency ?? 'USD',
        interval,
        active,
      };
      return tier
        ? api.patch(`/membership-tiers/${tier.id}`, body)
        : api.post('/membership-tiers', body);
    },
    onSuccess: onSaved,
    onError: (e) => setError(e instanceof ApiClientError ? e.message : 'Save failed'),
  });

  const deleteMut = useMutation({
    mutationFn: () => api.delete(`/membership-tiers/${tier!.id}`),
    onSuccess: onSaved,
  });

  return (
    <Modal onClose={onClose} title={tier ? 'Edit tier' : 'New tier'}>
      <form
        onSubmit={(e) => {
          e.preventDefault();
          if (!name) return setError('Name required');
          saveMut.mutate();
        }}
        style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-3)' }}
      >
        <Field label="Name *">
          <Input value={name} onChange={setName} autoFocus />
        </Field>
        <Field label="Description">
          <Textarea value={description} onChange={setDescription} rows={3} />
        </Field>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 'var(--space-2)' }}>
          <Field label="Price">
            <Input value={price} onChange={setPrice} type="number" step="0.01" min={0} />
          </Field>
          <Field label="Interval">
            <Select
              value={interval}
              onChange={setInterval}
              options={[
                { value: 'month', label: 'Monthly' },
                { value: 'year', label: 'Yearly' },
              ]}
            />
          </Field>
        </div>
        <label style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)', fontSize: 'var(--text-sm)' }}>
          <input type="checkbox" checked={active} onChange={(e) => setActive(e.target.checked)} />
          Active and accepting subscribers
        </label>
        <ErrorBanner error={error} />
        <div style={{ display: 'flex', justifyContent: 'space-between' }}>
          {tier ? (
            <Button variant="danger" onClick={() => deleteMut.mutate()} disabled={deleteMut.isPending}>
              Delete
            </Button>
          ) : (
            <span />
          )}
          <div style={{ display: 'flex', gap: 'var(--space-2)' }}>
            <Button variant="ghost" onClick={onClose}>
              Cancel
            </Button>
            <Button type="submit" disabled={saveMut.isPending}>
              {saveMut.isPending ? 'Saving…' : 'Save'}
            </Button>
          </div>
        </div>
      </form>
    </Modal>
  );
}
